// Bestiary species sheet: everything about one species on one page.
import { huntPower } from '../shared/power.ts';
import { CONFIG } from '../shared/config.ts';
import { SPECIES, caughtLevel } from '../shared/species.ts';
import { GROUNDS, AREA, REGIONS } from '../shared/grounds.ts';
import { ITEM, ITEMS, STAR_ITEM, stoneDrops, elementStone, ascensionStone } from '../shared/items.ts';
import { STAT_NAMES, type Rolls } from '../shared/rolls.ts';
import { ratingOf, statWeights } from '../shared/rating.ts';
import { gradeBadge } from './badges.ts';
import { killXp, huntKillCoins } from '../shared/xp.ts';
import { speciesCatchChance } from '../shared/capture.ts';

import { S } from './state.ts';
import { auraOneInNow } from './auracharm.ts';
import { spriteUrl, iconUrl } from './assets.ts';
import { esc, full, elChip, fighterOf } from './fmt.ts';
import { huntOddsTable } from './huntodds.ts';
import { speciesRecord } from './community.ts';
import { awakenSection } from './awakening.ts';

const pct = (x: number) => `${x >= 0.01 ? (x * 100).toFixed(2) : (x * 100).toFixed(3)}%`;
const avg = (over: Partial<Rolls> = {}): Rolls => ({ ivs: [16, 16, 16, 16, 16, 16], quality: 1, bloodline: 0, aura: false, trait: '', ...over });

export function speciesSheet(id: string): string {
  const sp = SPECIES[id];
  const d = S.trainer!.dex[id];
  const chain: string[] = [];
  // An Awakened form shows its base species' chain.
  let first = SPECIES[sp.awakenedFrom ?? id];
  while (true) { const prev = Object.values(SPECIES).find((x) => x.evolvesTo === first.id); if (!prev) break; first = prev; }
  for (let s: typeof sp | undefined = first; s; s = s.evolvesTo ? SPECIES[s.evolvesTo] : undefined) chain.push(s.id);
  const hunts = GROUNDS.filter((g) => g.species === id);
  const maxBase = Math.max(...sp.base);
  const w = statWeights(id);
  const perfect = avg({ ivs: [31, 31, 31, 31, 31, 31], quality: CONFIG.quality.max });
  const ratings = [
    ['Worst roll', ratingOf(avg({ ivs: [0, 0, 0, 0, 0, 0], quality: CONFIG.quality.min }), id)],
    ['Average roll', ratingOf(avg(), id)],
    ['Perfect roll', ratingOf(perfect, id)],
    ['Perfect, bloodline V', ratingOf({ ...perfect, bloodline: 4 }, id)],
    ['Perfect, bloodline V, aura', ratingOf({ ...perfect, bloodline: 4, aura: true }, id)],
  ] as const;
  const seals = ITEMS.filter((i) => i.kind === 'seal');
  const lv = Math.max(sp.captureCap, 1) >= 100 ? 100 : sp.captureCap;
  const p100 = (r: Rolls) => huntPower({ species: id, level: 100, rolls: r, stars: 0, relic: null });
  const huntRow = hunts[0];
  // The key drops on the region's hunts from tokenFrom to tokenTo, which may be a later hunt of this species than the first.
  const keyHunt = hunts.find((g) => g.bossToken);
  const lead = S.creatures.get(S.trainer!.team[0]);

  return `<div class="spsheet">
    <button class="btn tiny" data-act="dex-back">← Bestiary</button>
    <div class="sp-top">
      <div class="sp-forms"><figure><img src="${spriteUrl(id)}" alt=""><figcaption>Normal</figcaption></figure>
        <figure class="aura"><img src="${spriteUrl(id, true)}" alt=""><figcaption>✦ Aura</figcaption></figure></div>
      <div><h2>${esc(sp.name)}</h2>
        <div class="chips">${sp.elements.map(elChip).join('')}<span class="chip">${sp.rarity}</span><span class="chip">stage ${sp.stage}</span><span class="chip">${sp.style === 'might' ? 'Attack user' : 'Magic user'}</span></div>
        <div class="sp-facts">
          <div><span>Seen</span><b>${full(d?.seen ?? 0)}</b></div><div><span>Caught</span><b>${full(d?.caught ?? 0)}</b></div>${d?.bought ? `<div><span>Bought</span><b>${full(d.bought)}</b></div>` : ''}
          <div><span>Hunt level</span><b>${hunts.length ? hunts.map((g) => g.level).join(', ') : sp.awakened ? 'never: awakened only' : 'visitor only'}</b></div>
          <div><span>Level when caught</span><b>${huntRow ? caughtLevel(id, huntRow.level) : `up to ${sp.captureCap}`}</b></div>
          <div><span>XP per kill</span><b>${huntRow ? full(killXp(huntRow.level)) : '-'}</b></div>
          <div><span>Coins per kill</span><b>${huntRow ? full(huntKillCoins(huntRow.level)) : '-'}</b></div>
        </div>
        ${hunts.length ? `<p class="sub">Hunted in ${hunts.map((g) => esc(AREA[g.area].name)).join(', ')}.</p>` : ''}
        <button class="btn tiny" data-act="calc-species" data-id="${id}">Open in calculator</button></div>
    </div>

    ${speciesRecord(id)}

    <h4>Ascension</h4>
    <div class="sp-chain">${chain.map((cid, i) => {
      const c = SPECIES[cid];
      return `${i ? `<span class="arrow">→<small>Lv ${SPECIES[chain[i - 1]].evolveLevel} + ${esc(ITEM[ascensionStone(chain[i - 1])].name)}</small></span>` : ''}
        <div class="sp-node${cid === id ? ' on' : ''}" data-act="dex-open" data-id="${cid}"><img src="${spriteUrl(cid)}" alt=""><span>${esc(c.name)}</span></div>`;
    }).join('')}</div>
    ${awakenSection(id)}

    <div class="sp-cols">
      <section><h4>Base stats</h4>
        <div class="sp-stats">${STAT_NAMES.map((n, i) => `<span>${n}</span><span class="bar"><i style="width:${(sp.base[i] / maxBase) * 100}%"></i></span><b>${sp.base[i]}</b><span class="sub">${w[i] ? Math.round(w[i] * 100) + '% of talent' : 'unused'}</span>`).join('')}
        <span>Total</span><span></span><b>${sp.base.reduce((a, b) => a + b, 0)}</b><span></span></div>
        </section>
      <section><h4>Grade range</h4>
        <table class="tbl">${ratings.map(([k, v]) => `<tr><td>${k}</td><td>${gradeBadge(v, 'sm')} <b>${v}</b> / 100</td></tr>`).join('')}</table></section>
    </div>

    <h4>Bloodline</h4>
    <div class="sp-bl">${CONFIG.bloodline.map((b, i) => `<div class="bl${i}"><b>${b.name}</b><span>${b.pct}%</span><span>${b.mult > 1 ? `+${Math.round((b.mult - 1) * 100)}%` : '-'}</span>
      <span title="Power at level 100 with average rolls">⚔ ${full(p100(avg({ bloodline: i })))}</span><span class="aura" title="With an aura">✦ ⚔ ${full(p100(avg({ bloodline: i, aura: true })))}</span></div>`).join('')}</div>
    <p class="sub">Power at Lv 100, average rolls</p>

    <h4>Aura</h4>
    <div class="sp-facts"><div><span>Chance per catch</span><b>1 in ${full(auraOneInNow())}</b></div><div><span>Aura bonus</span><b>×${CONFIG.auraMult} every stat</b></div><div><span>Score</span><b>+${CONFIG.ratingPoints.aura}</b></div></div>

    ${huntRow && lead ? `<h4>Your lead on this hunt</h4>
    <p class="sub">${esc(SPECIES[lead.species].name)} Lv ${lead.level}${hunts.length > 1 ? ` against the Lv ${huntRow.level} hunt` : ''}.</p>
    ${huntOddsTable(fighterOf(lead, S.trainer!.halls), huntRow)}` : ''}

    ${huntRow ? `<h4>Drops</h4>
    <table class="tbl"><tr><th>Item</th><th>Chance</th><th>Quantity</th><th>Sells for</th></tr>
      ${stoneDrops(sp.id).filter((x) => SPECIES[sp.id].elements.some((el) => elementStone(el) === x.item)).map((x) => `<tr><td><img class="ico" src="${iconUrl(x.item)}" alt=""> ${esc(ITEM[x.item].name)}</td><td>${pct(x.p)}</td><td>1</td><td>Market</td></tr>`).join('')}
      <tr><td>Any other stone</td><td>${pct(CONFIG.stones.leak / CONFIG.stones.oneIn / 8)} each</td><td>1</td><td>Market</td></tr>
      ${keyHunt?.bossToken ? `<tr><td><img class="ico" src="${iconUrl(keyHunt.bossToken)}" alt=""> ${esc(ITEM[keyHunt.bossToken].name)}${keyHunt === huntRow ? '' : ` <span class="sub">Lv ${full(keyHunt.level)} hunt</span>`}</td><td>${pct(1 / CONFIG.bossTokenOneIn)}</td><td>1</td><td>Market</td></tr>` : ''}
      ${huntRow.index >= 9 ? `<tr><td><img class="ico" src="${iconUrl(STAR_ITEM[sp.region])}" alt=""> ${esc(ITEM[STAR_ITEM[sp.region]].name)}</td><td>${pct(1 / CONFIG.starMaterialOneIn)}</td><td>1</td><td>Market</td></tr>` : ''}
      ${huntRow.level >= 15 ? `<tr><td>Relic</td><td>${pct(1 / CONFIG.relicOneIn)}</td><td>1</td><td>Market</td></tr>` : ''}
    </table>` : ''}

    <h4>Catching</h4>
    <table class="tbl"><tr><th>Seal</th><th>Catch chance</th><th>Defeats per catch</th><th>Price</th><th>Aura</th></tr>
      ${seals.map((s) => { const c = speciesCatchChance(id, s.catchMult!); return `<tr><td><img class="ico" src="${iconUrl(s.id)}" alt=""> ${esc(s.name)}</td><td>${pct(c)}</td><td>${full(Math.ceil(1 / c))}</td><td>${s.shopPrice} coins</td><td>1 in ${full(Math.round(auraOneInNow() / c))} defeats</td></tr>`; }).join('')}
    </table>
    <p class="sub">Caught at Lv ${lv} or less</p>
  </div>`;
}
